/** @file Deleting vertices: re-indexed rest, hull shrunk, mesh retriangulated (no holes remain). */
import { meshOffsets } from '@pose2d/commands/animation/meshOffsets';
import type { MeshAtt } from '@pose2d/format/types';
import { blendSources, type TopologyPlan, type VertexSource } from './blendSources';
import { retriangulate } from './retriangulate';

/** Why a delete cannot be planned. */
export type DeleteRefusal = 'hullTooSmall' | 'noTriangles';

/** Whole groups of `size` whose indices all survive, re-indexed through `index` (−1 = deleted). */
function survivors(flat: readonly number[], size: number, index: Int32Array): number[] {
  const out: number[] = [];
  for (let i = 0; i + size <= flat.length; i += size) {
    const group = flat.slice(i, i + size).map((v) => index[v] ?? -1);
    if (group.every((v) => v >= 0)) out.push(...group);
  }
  return out;
}

/** How many of `count` vertices no triangle uses. */
const unused = (tris: readonly number[], count: number): number => count - new Set(tris).size;

/**
 * Removes `remove` (out-of-range indices ignored). The hull keeps its order minus the deleted hull vertices (at least
 * 3). Triangles: retriangulate over the remaining rest positions, unless the surviving old triangles leave fewer
 * vertices unconnected (a point on the new outline is skipped by the triangulation).
 */
export function deleteVerticesPlan(mesh: MeshAtt, remove: readonly number[]): TopologyPlan | DeleteRefusal {
  const count = mesh.uvs.length / 2;
  const gone = new Set(remove.filter((v) => Number.isInteger(v) && v >= 0 && v < count));
  const hull = mesh.hull - [...gone].filter((v) => v < mesh.hull).length;
  if (hull < 3) return 'hullTooSmall';
  const sources: VertexSource[] = [];
  const index = new Int32Array(count).fill(-1);
  for (let v = 0; v < count; v++) {
    if (gone.has(v)) continue;
    index[v] = sources.length;
    sources.push({ kind: 'old', v });
  }
  const fresh = retriangulate(blendSources.pairs(meshOffsets.rest(mesh), sources), hull);
  const kept = survivors(mesh.triangles, 3, index);
  const triangles = fresh !== null && fresh.skipped.length <= unused(kept, sources.length) ? fresh.triangles : kept;
  if (triangles.length < 3) return 'noTriangles';
  return { sources, triangles, hull, edges: mesh.edges === undefined ? null : survivors(mesh.edges, 2, index) };
}
